import clsx from 'clsx';
import { startOfDay, subDays } from 'date-fns';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { checkCellValueStyle, NumericalCellValueStyle } from '@toggle/helpers';

import { useCatalystInsightsList } from '~/react-query-hooks/insights/useCatalystInsights';
import { useGetV2ThemePerformance } from '~/react-query-hooks/themes/useGetV2ThemePerformance';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { formatPercentageNumber } from '~/widgets/earnings/utils/utils';
import { CatalystInsightPill } from '~/widgets/catalyst-insights-drawer/catalyst-insight-pill/CatalystInsightPill';

import { ThemeLogoIcon } from '../theme-logo-icon/ThemeLogoIcon';
import styles from './ThemeHeader.module.scss';

const numericalStyleToClass: Record<NumericalCellValueStyle, string> = {
  [NumericalCellValueStyle.Positive]: styles.bullish,
  [NumericalCellValueStyle.Negative]: styles.bearish,
  [NumericalCellValueStyle.Neutral]: styles.neutral,
};

interface ThemeHeaderProps {
  themeId: string;
  themeName: string;
  logo_url?: string;
  isCondensed?: boolean;
}

export const ThemeHeader = ({
  themeId,
  themeName,
  logo_url,
  isCondensed = false,
}: ThemeHeaderProps) => {
  const { t } = useTranslation('widget');

  const fromDate = useMemo(() => startOfDay(subDays(new Date(), 3)), []);
  const { data: catalystInsightsData } = useCatalystInsightsList({
    themes: [themeId],
    categories: ['market_wide'],
    from: fromDate.toISOString(),
    page: 1,
    page_size: 1,
  });

  const { data: performanceData } = useGetV2ThemePerformance({
    ids: themeId,
    intraday: true,
  });

  const intradayData = performanceData?.themes?.[0]?.intraday;
  const intradayValue = intradayData?.current;
  const numericalStyle = checkCellValueStyle(intradayValue);

  const formattedDate = useMemo(() => {
    if (!intradayData?.last_updated) {
      return undefined;
    }

    try {
      return new Intl.DateTimeFormat(navigator.language, {
        day: 'numeric',
        month: 'short',
      }).format(new Date(intradayData.last_updated));
    } catch {
      return undefined;
    }
  }, [intradayData?.last_updated]);

  return (
    <div className={styles.flexRow}>
      <div
        className={clsx(styles.logoAndTitle, {
          [styles.condensed]: isCondensed,
        })}
      >
        <ThemeLogoIcon
          logo_url={logo_url}
          name={themeName}
          key={themeName}
          isCondensed={isCondensed}
        />
        <div
          className={clsx(styles.nameAndReturn, {
            [styles.condensed]: isCondensed,
          })}
        >
          <span
            className={clsx(styles.titleText, {
              [styles.condensed]: isCondensed,
            })}
          >
            {themeName}
          </span>
          {intradayValue !== undefined &&
            (isCondensed ? (
              <span
                className={clsx(
                  styles.returnValue,
                  styles.condensed,
                  numericalStyleToClass[numericalStyle]
                )}
              >
                {formatPercentageNumber({
                  value: intradayValue,
                  includePercentSign: true,
                  addPlus: true,
                })}
              </span>
            ) : (
              <div className={styles.returnRow}>
                <span
                  className={clsx(
                    styles.returnValue,
                    numericalStyleToClass[numericalStyle]
                  )}
                >
                  {formatPercentageNumber({
                    value: intradayValue,
                    includePercentSign: true,
                    addPlus: true,
                  })}
                </span>
                {formattedDate && (
                  <span className={styles.returnLabel}>
                    {t('widget:themeOverview.asOfDate', {
                      date: formattedDate,
                    })}
                  </span>
                )}
              </div>
            ))}
        </div>
      </div>
      {!!catalystInsightsData?.length && (
        <CatalystInsightPill
          insightId={catalystInsightsData[0].id}
          themeId={themeId}
          subCategory={catalystInsightsData[0].sub_category}
          isClickable
          source={POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW}
          category={catalystInsightsData[0].category}
        />
      )}
    </div>
  );
};
